import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Organization } from '@/types/events';
import { isMarketplaceVisibleEvent } from '@/utils/isMarketplaceVisibleEvent';

type EventRow = Record<string, unknown> & {
  id: string;
  name: string;
  location: string | null;
  status: string | null;
  image: string | null;
  start_date: string | null;
  end_date: string | null;
  departments: string[] | null;
  time_intervals: string[] | null;
  created_at: string;
  created_by: string;
  slots: number | null;
  is_public: boolean;
  volunteers?: { count: number } | { count: number }[];
};

const baseSelect =
  'id, name, image, location, start_date, end_date, status, slots, departments, time_intervals, is_public, created_by, created_at';

function mergeDedupeSortByCreatedAt(a: Organization[], b: Organization[]): Organization[] {
  const byId = new Map<string, Organization>();
  for (const e of [...a, ...b]) {
    if (!byId.has(e.id)) byId.set(e.id, e);
  }
  return [...byId.values()].sort(
    (x, y) =>
      new Date(y.created_at ?? 0).getTime() - new Date(x.created_at ?? 0).getTime()
  );
}

async function fetchOrganizationEventsForVisibility(
  organizationId: string,
  volunteerUserId: string | null,
  isPublic: boolean
): Promise<Organization[]> {
  let data: EventRow[];
  const countByEventId: Record<string, number> = {};

  const { data: rawWithCount, error: embedError } = await supabase
    .from('events')
    .select(`${baseSelect}, volunteers(count)`)
    .eq('is_active', true)
    .eq('is_public', isPublic)
    .eq('created_by', organizationId)
    .order('created_at', { ascending: false });

  if (embedError || !rawWithCount) {
    const { data: rawPlain, error: plainError } = await supabase
      .from('events')
      .select(baseSelect)
      .eq('is_active', true)
      .eq('is_public', isPublic)
      .eq('created_by', organizationId)
      .order('created_at', { ascending: false });
    if (plainError) throw plainError;
    data = rawPlain as EventRow[];
    const eventIds = data.map((e) => e.id);
    if (eventIds.length > 0) {
      const volunteersRes = await supabase
        .from('volunteers')
        .select('event_id')
        .in('event_id', eventIds)
        .eq('is_active', true);
      for (const row of volunteersRes.data ?? []) {
        const id = (row as { event_id: string }).event_id;
        countByEventId[id] = (countByEventId[id] ?? 0) + 1;
      }
    }
  } else {
    data = rawWithCount as EventRow[];
  }

  if (!data || data.length === 0) return [];

  data = data.filter(isMarketplaceVisibleEvent);
  if (data.length === 0) return [];

  const eventIds = data.map((e) => e.id);
  const { data: applicationsData } = volunteerUserId
    ? await supabase
        .from('volunteer_applications')
        .select('event_id, id, status')
        .eq('volunteer_id', volunteerUserId)
        .in('event_id', eventIds)
    : { data: null };

  const applicationByEventId: Record<string, { id: string; status: string }> = {};
  for (const row of applicationsData ?? []) {
    const r = row as { event_id: string; id: string; status: string };
    applicationByEventId[r.event_id] = { id: r.id, status: r.status };
  }

  const activeVolunteerEventIds = new Set<string>();
  if (volunteerUserId && eventIds.length > 0) {
    const { data: volunteerRows } = await supabase
      .from('volunteers')
      .select('event_id')
      .eq('volunteer_id', volunteerUserId)
      .eq('is_active', true)
      .in('event_id', eventIds);
    for (const row of volunteerRows ?? []) {
      activeVolunteerEventIds.add((row as { event_id: string }).event_id);
    }
  }

  return data.map((event) => {
    let count: number;
    if (Object.keys(countByEventId).length > 0) {
      count = countByEventId[event.id] ?? 0;
    } else {
      const vol = event.volunteers;
      count = vol == null ? 0 : Array.isArray(vol) ? (vol[0]?.count ?? 0) : (vol as { count: number }).count;
    }
    return {
      id: event.id,
      name: event.name,
      location: event.location ?? '',
      status: event.status ?? 'Active',
      image: event.image ?? null,
      start_date: event.start_date,
      end_date: event.end_date,
      departments: event.departments ?? [],
      time_intervals: event.time_intervals ?? [],
      created_at: event.created_at,
      created_by: event.created_by,
      slots: event.slots ?? 0,
      volunteerCount: count,
      userApplication: applicationByEventId[event.id] ?? null,
      isAlreadyVolunteer: activeVolunteerEventIds.has(event.id),
      is_public: event.is_public,
    };
  }) as Organization[];
}

async function fetchOrganizationVisibleEvents(
  organizationId: string,
  volunteerUserId: string | null,
  includePrivateWhenMember: boolean
): Promise<Organization[]> {
  const publicRows = await fetchOrganizationEventsForVisibility(organizationId, volunteerUserId, true);
  if (!includePrivateWhenMember) return publicRows;
  const privateRows = await fetchOrganizationEventsForVisibility(organizationId, volunteerUserId, false);
  return mergeDedupeSortByCreatedAt(privateRows, publicRows);
}

/**
 * Active events for an org profile modal: public for everyone; plus private when the viewer is an accepted member (RLS).
 */
export function useOrganizationPublicEvents(
  organizationId: string | null,
  volunteerUserId: string | null,
  includePrivateWhenMember = false
) {
  const [events, setEvents] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setEvents([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchOrganizationVisibleEvents(organizationId, volunteerUserId, includePrivateWhenMember)
      .then((list) => {
        if (!cancelled) setEvents(list);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load events');
          setEvents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, volunteerUserId, includePrivateWhenMember]);

  return { events, loading, error };
}
